import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  addMonths, 
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays
} from 'date-fns';
import { Calendar as CalendarIcon, AlertCircle, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useOutlookCalendar } from '@/hooks/useOutlookCalendar';
import { EventModal } from '@/components/crm/EventModal';
import { 
  CalendarGrid, 
  CalendarSidebar, 
  CalendarHeader,
  CalendarDayView,
  CalendarWeekView
} from '@/components/crm/calendar';
import type { CalendarView } from '@/components/crm/calendar';

// Hoe lang een net opgehaald maandvenster met rust wordt gelaten. Een minuut is
// genoeg om doorbladeren gratis te houden (maandwissels gaan in seconden) en kort
// genoeg om een agenda die openstaat mee te laten bewegen met Outlook.
const WINDOW_COOLDOWN_MS = 60_000;

export default function CRMCalendar() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showEventModal, setShowEventModal] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [view, setView] = useState<CalendarView>('month');
  
  const { events, isConnected, loading, syncing, syncEvents, loadCachedEvents, deleteEvent } =
    useOutlookCalendar();

  // Wanneer elk maandvenster deze sessie voor het laatst bij Microsoft is
  // opgehaald. Dit was een Set van vensters die "al gedaan" waren: een keer per
  // sessie en daarna nooit meer. Dat was nodig zolang een tweede ronde het hele
  // venster opnieuw ophaalde -- 28 tot 42 dagen aan afspraken. Opnieuw nagerekend
  // over de dertien vensters 2026-09 t/m 2027-09 (ronde een telde hier mis): 42
  // dagen in drie vensters (2026-11, 2027-05, 2027-08), 28 in een (2027-02, dat
  // precies op maandag begint en op zondag eindigt) en 35 in de overige negen.
  // Sinds de delta-sync kost een herhaalde ronde alleen nog de wijzigingen, en
  // nul afspraken als er niets veranderd is.
  // Daarom mag een venster na de koeltijd wel opnieuw: wie een uur later
  // terugbladert naar deze maand ziet nu wat er intussen in Outlook is gewijzigd
  // of afgezegd, in plaats van het beeld van bij het openen van de pagina.
  // Heen en weer bladeren tussen twee maanden blijft binnen de koeltijd gratis.
  const windowLastSyncedAt = useRef<Map<string, number>>(new Map());

  function huidigVenster() {
    return {
      start: startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 }),
      end: endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 }),
    };
  }

  useEffect(() => {
    if (!isConnected) return;

    const { start, end } = huidigVenster();
    const sleutel = `${start.toISOString()}|${end.toISOString()}`;

    // De cache vult het scherm meteen; de sync erna is aanvulling, geen
    // voorwaarde. Zo staat er nooit een leeg raster te wachten op Microsoft.
    void loadCachedEvents(start, end).then(() => {
      const lastSyncedAt = windowLastSyncedAt.current.get(sleutel) ?? 0;
      if (Date.now() - lastSyncedAt < WINDOW_COOLDOWN_MS) return;
      // Vóór de aanroep gezet en niet erna: anders start de dubbele
      // effect-aanroep van React StrictMode twee syncs op hetzelfde venster.
      windowLastSyncedAt.current.set(sleutel, Date.now());
      void handleSync({ stil: true });
    });
  }, [currentDate, isConnected, loadCachedEvents]);

  async function handleSync(opties?: { stil?: boolean }) {
    setSyncError(null);

    try {
      const { start, end } = huidigVenster();
      // Een handmatige klik op Synchroniseer haalt altijd opnieuw op, ook binnen
      // de koeltijd, en zet de teller van dit venster daarna op nu.
      windowLastSyncedAt.current.set(`${start.toISOString()}|${end.toISOString()}`, Date.now());
      await syncEvents(start, end, opties);
    } catch (error: any) {
      console.error('[CRMCalendar] Sync error:', error);
      setSyncError(error.message || 'Kon agenda niet synchroniseren');
    }
  }

  function handleNavigate(direction: 'prev' | 'next') {
    setCurrentDate(prev => {
      switch (view) {
        case 'day':
          return direction === 'next' ? addDays(prev, 1) : subDays(prev, 1);
        case 'week':
          return direction === 'next' ? addWeeks(prev, 1) : subWeeks(prev, 1);
        default:
          return direction === 'next' ? addMonths(prev, 1) : subMonths(prev, 1);
      }
    });
  }

  function handleToday() {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  }

  function handleViewChange(newView: CalendarView) {
    setView(newView);
  }

  function handleDayClick(date: Date) {
    setSelectedDate(date);
    // Optionally switch to day view when clicking on a day
    if (view === 'week') {
      setCurrentDate(date);
    }
  }

  function handleDeleteEvent(eventId: string) {
    deleteEvent(eventId);
  }

  // Not connected state
  if (!isConnected && !loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <WifiOff className="h-8 w-8 text-muted-foreground" />
            </div>
            <CardTitle className="text-xl">Outlook niet verbonden</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted-foreground mb-6">
              Verbind je Outlook agenda om afspraken te synchroniseren en te beheren.
            </p>
            <Button 
              onClick={() => navigate('/crm/settings')}
              className="bg-energy-green hover:bg-energy-green/90"
            >
              Naar Instellingen
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-energy-green/20 border-t-energy-green animate-spin" />
          </div>
          <p className="text-muted-foreground">Agenda laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header with view switcher */}
      <CalendarHeader
        currentDate={currentDate}
        view={view}
        onViewChange={handleViewChange}
        onNavigate={handleNavigate}
        onToday={handleToday}
        onSync={() => handleSync()}
        syncing={syncing}
      />

      {/* Sync error alert */}
      {syncError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{syncError}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleSync()}
              className="ml-2"
            >
              Opnieuw proberen
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Main content - varies by view */}
      {view === 'month' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar Grid - takes 2 columns on large screens */}
          <div className="lg:col-span-2">
            <CalendarGrid
              currentDate={currentDate}
              events={events}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
            />
          </div>

          {/* Sidebar - Events for selected day */}
          <div className="lg:col-span-1 h-[600px] lg:h-auto">
            <CalendarSidebar
              selectedDate={selectedDate}
              events={events}
              onDeleteEvent={handleDeleteEvent}
              onCreateEvent={() => setShowEventModal(true)}
            />
          </div>
        </div>
      )}

      {view === 'week' && (
        <CalendarWeekView
          currentDate={currentDate}
          events={events}
          onEventClick={(event) => console.log('Event clicked:', event)}
          onDayClick={handleDayClick}
        />
      )}

      {view === 'day' && (
        <CalendarDayView
          currentDate={currentDate}
          events={events}
          onEventClick={(event) => console.log('Event clicked:', event)}
        />
      )}

      {/* Event creation modal */}
      <EventModal 
        open={showEventModal} 
        onOpenChange={setShowEventModal}
        onSuccess={() => handleSync()}
      />
    </div>
  );
}
